import React, { Component } from "react";
import { Modal, ModalHeader, ModalBody, Button } from "reactstrap";
import PropTypes from "prop-types";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";
import { BrowserMultiFormatReader } from "@zxing/browser";
import "./BarcodeScanner.css";

class BarcodeScanner extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isScanning: false,
      error: null,
      detectedCode: null,
      showPermissionPrompt: true,
    };
    this.videoRef = React.createRef();
    this.codeReader = null;
    this.scannerControls = null;
    this.preferredDeviceId = null;

    this._startScanning = this._startScanning.bind(this);
    this._stopScanning = this._stopScanning.bind(this);
    this._handleClose = this._handleClose.bind(this);
    this._handleAllowCamera = this._handleAllowCamera.bind(this);
    this._getRearCameraDeviceId = this._getRearCameraDeviceId.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (!this.props.isOpen && prevProps.isOpen) {
      this._stopScanning();
      this.setState({ showPermissionPrompt: true, error: null });
    }
  }

  componentWillUnmount() {
    this._stopScanning();
  }

  async _getRearCameraDeviceId() {
    try {
      // Create temporary codeReader to list devices (same approach as reference code)
      const tempReader = new BrowserMultiFormatReader();
      const videoInputDevices = await tempReader.listVideoInputDevices();

      if (videoInputDevices.length === 0) {
        return undefined;
      }

      // Try to find rear/back/environment facing camera
      const rearCamera = videoInputDevices.find((device) => {
        const label = device.label.toLowerCase();
        return (
          label.includes("back") ||
          label.includes("rear") ||
          label.includes("environment") ||
          label.includes("facing back")
        );
      });

      if (rearCamera) {
        console.log("Using rear camera:", rearCamera.label);
        return rearCamera.deviceId;
      }

      // Fallback to first available camera (like reference code)
      console.log("Using first available camera:", videoInputDevices[0].label);
      return videoInputDevices[0].deviceId;
    } catch (err) {
      console.warn("Could not enumerate devices:", err);
      return undefined;
    }
  }

  async _handleAllowCamera() {
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: "camera",
        });

        if (permissionStatus.state === "denied") {
          this.setState({
            error:
              'Camera permission was previously denied.\n\nTo enable camera access:\n1. Tap the lock/info icon in the address bar\n2. Go to Permissions → Camera\n3. Change from "Blocked" to "Ask" or "Allow"\n4. Refresh the page and try again',
            showPermissionPrompt: true,
          });
          return;
        }
      } catch (err) {
        // Permission API not supported or error, continue anyway
      }
    }

    // Get preferred rear camera device ID
    this.preferredDeviceId = await this._getRearCameraDeviceId();

    this.setState({ showPermissionPrompt: false }, () => {
      this._startScanning();
    });
  }

  async _startScanning() {
    try {
      const hints = new Map();
      hints.set(DecodeHintType.TRY_HARDER, true);
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE]);

      this.codeReader = new BrowserMultiFormatReader(hints, {
        delayBetweenScanAttempts: 100,
      });

      this.setState({ isScanning: true, error: null });

      const deviceId = this.preferredDeviceId || undefined;

      this.scannerControls = await this.codeReader.decodeFromVideoDevice(
        deviceId,
        this.videoRef.current,
        (result, err) => {
          if (result) {
            const scannedText = result.text.trim();

            if (!scannedText.toUpperCase().startsWith("CG-")) {
              this.setState({
                error: `Invalid QR code format!\n\nExpected: CG-XXXXX\nScanned: ${scannedText}\n\nPlease scan a valid CG ID QR code.`,
              });

              return;
            }

            this._stopScanning();

            this.props.onScan(scannedText);

            this.props.onToggle();
          }

          if (err && err.name !== "NotFoundException") {
            // Silently ignore other errors during scanning
          }
        }
      );
    } catch (err) {
      let errorMessage = "Failed to access camera. ";

      if (
        err.name === "NotAllowedError" ||
        err.name === "PermissionDeniedError"
      ) {
        errorMessage =
          'Camera permission was denied.\n\nTo enable camera access:\n1. Tap the lock/info icon in the address bar\n2. Go to Permissions → Camera\n3. Change from "Blocked" to "Ask" or "Allow"\n4. Refresh the page and try again';
      } else if (
        err.name === "NotFoundError" ||
        err.name === "DevicesNotFoundError"
      ) {
        errorMessage += "No camera found on this device.";
      } else if (
        err.name === "NotReadableError" ||
        err.name === "TrackStartError"
      ) {
        errorMessage += "Camera is already in use by another application.";
      } else {
        errorMessage +=
          err.message ||
          "Please ensure camera permissions are granted and try again.";
      }

      this.setState({
        error: errorMessage,
        isScanning: false,
        showPermissionPrompt: true,
      });
    }
  }

  _stopScanning() {
    if (this.scannerControls) {
      this.scannerControls.stop();
      this.scannerControls = null;
    }

    if (this.codeReader) {
      this.codeReader = null;
    }

    this.preferredDeviceId = null;

    this.setState({ isScanning: false });
  }

  _handleClose() {
    this._stopScanning();
    this.props.onToggle();
  }

  render() {
    return (
      <Modal isOpen={this.props.isOpen} toggle={this._handleClose} size="lg">
        <ModalHeader toggle={this._handleClose}>Scan QR Code</ModalHeader>
        <ModalBody>
          <div className="barcode-scanner-container">
            {this.state.showPermissionPrompt ? (
              <div className="permission-prompt">
                <div className="text-center">
                  <i
                    className="icon-camera"
                    style={{
                      fontSize: "64px",
                      color: "#ffc107",
                      marginBottom: "20px",
                    }}
                  ></i>
                  <h5>Camera Access Required</h5>
                  <p className="text-muted">
                    This feature needs access to your camera to scan QR codes.
                    <br />
                    Click "Allow Camera" to grant permission.
                  </p>
                  {this.state.error && (
                    <div className="alert alert-danger mt-3">
                      {this.state.error}
                    </div>
                  )}
                  <div className="mt-4">
                    <Button
                      color="warning"
                      onClick={this._handleAllowCamera}
                      className="mr-2"
                    >
                      <i className="icon-camera"></i> Allow Camera
                    </Button>
                    <Button color="secondary" onClick={this._handleClose}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {this.state.error && (
                  <div className="alert alert-danger mb-3">
                    {this.state.error}
                  </div>
                )}
                <div className="scanner-viewport">
                  <video
                    ref={this.videoRef}
                    className="barcode-video"
                    playsInline
                  />
                  <div className="scanner-overlay">
                    <div className="scanner-frame"></div>
                  </div>
                </div>
                <div className="scanner-info">
                  <p className="text-center mt-3">
                    {this.state.isScanning ? (
                      <>
                        <i className="fa fa-qrcode"></i> Position the QR code
                        within the frame
                        <br />
                        <small className="text-muted">
                          Scan works from various angles - adjust camera
                          position if needed
                        </small>
                      </>
                    ) : (
                      "Starting camera..."
                    )}
                  </p>
                  <div className="text-center mt-3">
                    <Button color="secondary" onClick={this._handleClose}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </ModalBody>
      </Modal>
    );
  }
}

BarcodeScanner.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onScan: PropTypes.func.isRequired,
};

export default BarcodeScanner;
